import { useState, useCallback, useEffect, useRef } from 'react';
import * as Cesium from 'cesium';
import { createBoundingPolygon } from '../utils/polygonUtils';
import { calculatePolygonAreaM2 } from '../utils/geometryUtils';
import { makePolylineWidthProperty } from '../utils/cesiumEntityFactory';
import { formatDistance } from '../utils/units';
// On-brand label chrome (sand pill, forest text) — matches the finished-polygon
// side labels. Shared module constants so the draw-time labels read identically.
const LABEL_FOREST = Cesium.Color.fromCssColorString('#263328');
const LABEL_SAND = Cesium.Color.fromCssColorString('#EEE9E1');
// Build a sand-pill side-length label entity with CallbackProperty geometry.
const addLengthLabel = (ds, position, text, show) => ds.entities.add({
    position: new Cesium.CallbackProperty(() => position(), false),
    label: {
        text: new Cesium.CallbackProperty(() => text(), false),
        show: new Cesium.CallbackProperty(() => show(), false),
        font: '600 11px -apple-system, system-ui, sans-serif',
        fillColor: LABEL_FOREST,
        showBackground: true,
        backgroundColor: LABEL_SAND.withAlpha(0.92),
        backgroundPadding: new Cesium.Cartesian2(7, 4),
        style: Cesium.LabelStyle.FILL,
        verticalOrigin: Cesium.VerticalOrigin.CENTER,
        horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
    },
});
export const usePolygonDrawing = ({ viewer, isActive, drawMode = 'freeform', showLabels = true, lengthUnit = 'ft', centroidLabel = '', centroidLabelFormatter, onPolygonChange, onFinishRequested, onRectangleComplete, onEntityClick, pointColor = Cesium.Color.YELLOW, pointOutlineColor = Cesium.Color.BLACK, polygonFillColor = Cesium.Color.BLUE.withAlpha(0.3), polygonOutlineColor = Cesium.Color.BLUE, transformPoint }) => {
    const [points, setPointsState] = useState([]);
    const [cartesianPoints, setCartesianPoints] = useState([]);
    const [areaM2, setAreaM2] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    // -1 = nothing selected. Tracks the vertex shown in "active" (yellow)
    // styling so the panel can offer a Delete-vertex action.
    const [selectedVertexIndex, setSelectedVertexIndexState] = useState(-1);
    // True in rectangle mode after the first corner is placed, while the second
    // is being positioned. Drives the rectangle mid-draw HUD.
    const [rectanglePending, setRectanglePending] = useState(false);
    // Refs for Cesium objects
    const dataSourceRef = useRef(null);
    const handlerRef = useRef(null);
    const pointEntitiesRef = useRef([]);
    const polygonEntityRef = useRef(null);
    const draggedIndexRef = useRef(-1);
    // Phantom-vertex-on-edge state. A separate pickable outline polyline +
    // a ghost vertex point. While the cursor is over the outline, the
    // phantom paints at the cursor's terrain projection and clicking it
    // inserts a real vertex on that edge (between the two vertices the edge
    // connects). The outline polyline exists alongside the polygon entity's
    // built-in outline so we have a pickable target — Cesium polygon
    // entities pick on their entire fill area, which is too broad.
    const outlinePolylineRef = useRef(null);
    const phantomEntityRef = useRef(null);
    const phantomTargetRef = useRef(null);
    const isActiveRef = useRef(false);
    // Index of the vertex the cursor is currently over (green-on-hover), or
    // -1 if none. Distinct from selectedVertexIndex (yellow); the selected
    // vertex always takes precedence visually.
    const hoveredVertexIndexRef = useRef(-1);
    // Refs for current state values (to avoid recreating callbacks on state change)
    const pointsRef = useRef(points);
    const cartesianPointsRef = useRef(cartesianPoints);
    const isDraggingRef = useRef(isDragging);
    const selectedVertexIndexRef = useRef(-1);
    const onPolygonChangeRef = useRef(onPolygonChange);
    const onFinishRequestedRef = useRef(onFinishRequested);
    const onRectangleCompleteRef = useRef(onRectangleComplete);
    const onEntityClickRef = useRef(onEntityClick);
    const drawModeRef = useRef(drawMode);
    const showLabelsRef = useRef(showLabels);
    const lengthUnitRef = useRef(lengthUnit);
    const centroidLabelRef = useRef(centroidLabel);
    const centroidLabelFormatterRef = useRef(centroidLabelFormatter);
    // Live planar area mirror so the centroid label's CallbackProperty can format
    // the current area each frame without recomputing it.
    const areaM2Ref = useRef(areaM2);
    // The single centroid area label entity (created with the data source,
    // shown only while it resolves to non-empty text and the polygon is closed).
    const centroidLabelEntityRef = useRef(null);
    // Live side-length labels: one per freeform segment (rebuilt on point-count
    // change), plus width/height for the rectangle preview.
    const segmentLabelsRef = useRef([]);
    const rectWidthLabelRef = useRef(null);
    const rectHeightLabelRef = useRef(null);
    // Rectangle-mode state. cornerA is the first click; cursor follows the
    // mouse for the live rubber-band; the preview fill + outline + corner
    // marker entities render the in-progress rectangle. All ref-driven so the
    // CallbackProperty geometry updates per-frame without React renders.
    const rectCornerARef = useRef(null);
    const rectCursorRef = useRef(null);
    const rectFillEntityRef = useRef(null);
    const rectOutlineEntityRef = useRef(null);
    const rectMarkerEntityRef = useRef(null);
    const doubleClickGuardRef = useRef(false);
    const addPointRef = useRef(() => { });
    const removePointRef = useRef(() => { });
    const updatePointPositionRef = useRef(() => { });
    // Keep refs in sync with state
    useEffect(() => { pointsRef.current = points; }, [points]);
    useEffect(() => { cartesianPointsRef.current = cartesianPoints; }, [cartesianPoints]);
    // Keep the optional vertex transform (e.g. DEM AOI clamp) current for the
    // ref-based add/insert/drag callbacks below.
    const transformPointRef = useRef(transformPoint);
    useEffect(() => { transformPointRef.current = transformPoint; }, [transformPoint]);
    useEffect(() => { isDraggingRef.current = isDragging; }, [isDragging]);
    useEffect(() => { selectedVertexIndexRef.current = selectedVertexIndex; }, [selectedVertexIndex]);
    useEffect(() => { onPolygonChangeRef.current = onPolygonChange; }, [onPolygonChange]);
    useEffect(() => { onFinishRequestedRef.current = onFinishRequested; }, [onFinishRequested]);
    useEffect(() => { onRectangleCompleteRef.current = onRectangleComplete; }, [onRectangleComplete]);
    useEffect(() => { onEntityClickRef.current = onEntityClick; }, [onEntityClick]);
    useEffect(() => { isActiveRef.current = isActive; }, [isActive]);
    // Label show-state + unit are read live by the label CallbackProperties, so
    // toggling the preference flips them without rebuilding any entities.
    useEffect(() => { showLabelsRef.current = showLabels; }, [showLabels]);
    useEffect(() => { lengthUnitRef.current = lengthUnit; }, [lengthUnit]);
    useEffect(() => { centroidLabelRef.current = centroidLabel; }, [centroidLabel]);
    useEffect(() => { centroidLabelFormatterRef.current = centroidLabelFormatter; }, [centroidLabelFormatter]);
    useEffect(() => { areaM2Ref.current = areaM2; }, [areaM2]);
    // Visual styling helpers — applied directly to entity properties so they
    // take effect mid-frame without a React render round-trip.
    const applyVertexHighlight = useCallback((index) => {
        const entity = pointEntitiesRef.current[index];
        if (!(entity === null || entity === void 0 ? void 0 : entity.point))
            return;
        entity.point.color = new Cesium.ConstantProperty(Cesium.Color.YELLOW);
        entity.point.outlineColor = new Cesium.ConstantProperty(Cesium.Color.BLACK);
        entity.point.outlineWidth = new Cesium.ConstantProperty(3);
        entity.point.pixelSize = new Cesium.ConstantProperty(11);
    }, []);
    const applyVertexDefault = useCallback((index) => {
        const entity = pointEntitiesRef.current[index];
        if (!(entity === null || entity === void 0 ? void 0 : entity.point))
            return;
        entity.point.color = new Cesium.ConstantProperty(pointColor);
        entity.point.outlineColor = new Cesium.ConstantProperty(pointOutlineColor);
        entity.point.outlineWidth = new Cesium.ConstantProperty(2);
        entity.point.pixelSize = new Cesium.ConstantProperty(10);
    }, [pointColor, pointOutlineColor]);
    const applyVertexHover = useCallback((index) => {
        const entity = pointEntitiesRef.current[index];
        if (!(entity === null || entity === void 0 ? void 0 : entity.point))
            return;
        entity.point.color = new Cesium.ConstantProperty(Cesium.Color.LIMEGREEN);
        entity.point.outlineColor = new Cesium.ConstantProperty(Cesium.Color.BLACK);
        entity.point.outlineWidth = new Cesium.ConstantProperty(2);
        entity.point.pixelSize = new Cesium.ConstantProperty(11);
    }, []);
    // Single setter that keeps the React state, the ref, and the on-map
    // visuals in lockstep. -1 means "nothing selected".
    const setSelectedVertexIndex = useCallback((next) => {
        const prev = selectedVertexIndexRef.current;
        if (prev === next)
            return;
        if (prev >= 0 && prev < pointEntitiesRef.current.length) {
            applyVertexDefault(prev);
        }
        if (next >= 0 && next < pointEntitiesRef.current.length) {
            applyVertexHighlight(next);
        }
        selectedVertexIndexRef.current = next;
        setSelectedVertexIndexState(next);
    }, [applyVertexDefault, applyVertexHighlight]);
    // Initialize data source + the phantom-insertion entities (outline
    // polyline that's pickable on its own, plus the ghost vertex). Both
    // live for the lifetime of the data source; visibility is driven by
    // refs in the MOUSE_MOVE handler.
    useEffect(() => {
        if (!viewer || viewer.isDestroyed())
            return;
        const dataSource = new Cesium.CustomDataSource('polygon-drawing');
        viewer.dataSources.add(dataSource);
        dataSourceRef.current = dataSource;
        // Visible perimeter outline + pick target for phantom-on-edge-hover.
        // Same renderer setup that cesiumEntityFactory.createPolygonOutline
        // uses for saved polygons (the blue project-boundary line, etc.),
        // since polygon.outline: true with CLAMP_TO_GROUND isn't reliable on
        // this terrain provider. CallbackProperty for positions so the line
        // tracks vertex add / drag / delete in-place.
        const outline = dataSource.entities.add({
            polyline: {
                positions: new Cesium.CallbackProperty(() => {
                    const pts = cartesianPointsRef.current;
                    if (pts.length < 2)
                        return [];
                    return pts.length >= 3 ? [...pts, pts[0]] : [...pts];
                }, false),
                width: makePolylineWidthProperty(2, viewer),
                material: new Cesium.ColorMaterialProperty(polygonOutlineColor),
                clampToGround: true,
            },
        });
        outlinePolylineRef.current = outline;
        // Ghost vertex shown at the cursor's projection on the hovered edge.
        // Hidden by default; positioned + shown by the MOUSE_MOVE handler.
        const phantom = dataSource.entities.add({
            show: false,
            position: new Cesium.ConstantPositionProperty(Cesium.Cartesian3.ZERO),
            point: {
                pixelSize: 11,
                color: Cesium.Color.YELLOW.withAlpha(0.55),
                outlineColor: Cesium.Color.BLACK.withAlpha(0.6),
                outlineWidth: 2,
                heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
            },
        });
        phantomEntityRef.current = phantom;
        // Centroid area label. Painted in the middle of the shape — the average of
        // the live vertices for a freeform polygon, or the diagonal midpoint for a
        // rectangle still being dragged (whose corners live in their own refs, not
        // the vertex buffer). Text comes from either the formatter (markup tool,
        // from the live area) or the pre-formatted string (delineation); both
        // resolve through effectiveCentroidText. Bold white with a dark outline so
        // it reads as the shape's centerpiece over the fill on any basemap.
        const rectPending = () => drawModeRef.current === 'rectangle' && !!rectCornerARef.current && !!rectCursorRef.current;
        const effectiveCentroidText = () => {
            if (cartesianPointsRef.current.length < 3 && !rectPending())
                return '';
            if (centroidLabelFormatterRef.current)
                return centroidLabelFormatterRef.current(areaM2Ref.current);
            return centroidLabelRef.current;
        };
        const centroidLabelEntity = dataSource.entities.add({
            position: new Cesium.CallbackProperty(() => {
                if (rectPending()) {
                    const a = rectCornerARef.current;
                    const c = rectCursorRef.current;
                    return Cesium.Cartesian3.fromDegrees((a.lng + c.lng) / 2, (a.lat + c.lat) / 2);
                }
                const pts = cartesianPointsRef.current;
                if (pts.length < 3)
                    return Cesium.Cartesian3.ZERO;
                const sum = pts.reduce((acc, p) => Cesium.Cartesian3.add(acc, p, acc), new Cesium.Cartesian3(0, 0, 0));
                return Cesium.Cartesian3.multiplyByScalar(sum, 1 / pts.length, sum);
            }, false),
            label: {
                // Same chrome as the perimeter side-length labels (addLengthLabel):
                // forest text on a sand pill, identical font, so the area reads as one
                // of the on-map measurements rather than a separate style.
                text: new Cesium.CallbackProperty(() => effectiveCentroidText(), false),
                show: new Cesium.CallbackProperty(() => effectiveCentroidText() !== '', false),
                font: '600 11px -apple-system, system-ui, sans-serif',
                fillColor: LABEL_FOREST,
                showBackground: true,
                backgroundColor: LABEL_SAND.withAlpha(0.92),
                backgroundPadding: new Cesium.Cartesian2(7, 4),
                style: Cesium.LabelStyle.FILL,
                verticalOrigin: Cesium.VerticalOrigin.CENTER,
                horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
                disableDepthTestDistance: Number.POSITIVE_INFINITY,
            },
        });
        centroidLabelEntityRef.current = centroidLabelEntity;
        return () => {
            if (viewer && !viewer.isDestroyed() && dataSourceRef.current) {
                viewer.dataSources.remove(dataSourceRef.current);
                dataSourceRef.current = null;
            }
            outlinePolylineRef.current = null;
            phantomEntityRef.current = null;
            phantomTargetRef.current = null;
            centroidLabelEntityRef.current = null;
        };
    }, [viewer]);
    // Update polygon visualization. Restored to the HEAD/committed pattern:
    // destroy and recreate the polygon entity on every vertex change, with
    // the polygon entity's built-in outline rendering the visible perimeter.
    const updatePolygon = useCallback(() => {
        if (!dataSourceRef.current || !viewer || viewer.isDestroyed())
            return;
        if (polygonEntityRef.current) {
            dataSourceRef.current.entities.remove(polygonEntityRef.current);
            polygonEntityRef.current = null;
        }
        const currentCartesian = cartesianPointsRef.current;
        if (currentCartesian.length >= 3) {
            const orderedPoints = createBoundingPolygon(currentCartesian);
            polygonEntityRef.current = dataSourceRef.current.entities.add({
                polygon: {
                    hierarchy: new Cesium.PolygonHierarchy(orderedPoints),
                    material: polygonFillColor,
                    // Outline rendered by the dedicated outline polyline (see init
                    // effect) — same approach saved polygons use via
                    // cesiumEntityFactory.createPolygonOutline. polygon.outline: true
                    // is unreliable with CLAMP_TO_GROUND on this terrain provider.
                    outline: false,
                    heightReference: Cesium.HeightReference.CLAMP_TO_GROUND
                }
            });
        }
    }, [viewer, polygonFillColor, polygonOutlineColor]);
    // Update polygon when points change
    useEffect(() => {
        updatePolygon();
    }, [cartesianPoints, updatePolygon]);
    // Freeform side-length labels — one per outline segment at its midpoint.
    // Rebuilt only when the point COUNT changes; positions/text are
    // CallbackProperty-driven so they track vertex drags without a rebuild, and
    // show-state is read live from showLabelsRef so the opt-out toggle is instant.
    // Rectangle mode has its own width/height labels (see ensureRectPreview).
    useEffect(() => {
        const ds = dataSourceRef.current;
        if (!ds)
            return;
        const clear = () => {
            segmentLabelsRef.current.forEach(e => ds.entities.remove(e));
            segmentLabelsRef.current = [];
        };
        clear();
        if (drawMode === 'rectangle')
            return;
        const n = cartesianPoints.length;
        if (n < 2)
            return;
        // 0→1 … (n-2)→(n-1), plus the closing (n-1)→0 once it's a polygon (n>=3),
        // matching the outline which closes at 3 points.
        const segCount = n >= 3 ? n : n - 1;
        for (let i = 0; i < segCount; i++) {
            const a = i;
            const b = (i + 1) % n;
            const mid = () => {
                const pts = cartesianPointsRef.current;
                if (!pts[a] || !pts[b])
                    return Cesium.Cartesian3.ZERO;
                return Cesium.Cartesian3.midpoint(pts[a], pts[b], new Cesium.Cartesian3());
            };
            const text = () => {
                const pts = cartesianPointsRef.current;
                if (!pts[a] || !pts[b])
                    return '';
                return formatDistance(Cesium.Cartesian3.distance(pts[a], pts[b]), lengthUnitRef.current);
            };
            segmentLabelsRef.current.push(addLengthLabel(ds, mid, text, () => showLabelsRef.current));
        }
        return clear;
    }, [cartesianPoints.length, drawMode, viewer]);
    // Returns the index of the edge whose midpoint is closest to a given
    // terrain position. Edges are 0→1, 1→2, …, (N-1)→0 (closing edge
    // included only when there are ≥3 vertices).
    const findNearestEdge = useCallback((cursor) => {
        const pts = cartesianPointsRef.current;
        if (pts.length < 2)
            return -1;
        let bestIdx = 0;
        let bestDist = Infinity;
        const closingExists = pts.length >= 3;
        for (let i = 0; i < pts.length; i++) {
            if (i === pts.length - 1 && !closingExists)
                continue;
            const a = pts[i];
            const b = pts[(i + 1) % pts.length];
            const midpoint = Cesium.Cartesian3.midpoint(a, b, new Cesium.Cartesian3());
            const d = Cesium.Cartesian3.distance(cursor, midpoint);
            if (d < bestDist) {
                bestDist = d;
                bestIdx = i;
            }
        }
        return bestIdx;
    }, []);
    // Insert a vertex at a specific array index — used by the phantom edge-
    // click to splice a new vertex between two existing ones. Edge i sits
    // between point[i] and point[(i+1) % N]; inserting on edge i lands the
    // new vertex at array index (i+1). For the closing edge this means
    // appending (index === N), preserving polygon closure.
    const insertPointAt = useCallback((insertIndex, position) => {
        var _a;
        if (!dataSourceRef.current || !viewer || viewer.isDestroyed())
            return;
        if (transformPointRef.current)
            position = transformPointRef.current(position);
        const cartographic = Cesium.Cartographic.fromCartesian(position);
        const coord = {
            lat: Cesium.Math.toDegrees(cartographic.latitude),
            lng: Cesium.Math.toDegrees(cartographic.longitude),
        };
        // Drop highlight on whoever was selected; the inserted vertex becomes
        // the new active one (parity with addPoint and with the path tool's
        // insert-on-segment behaviour).
        const prevSelected = selectedVertexIndexRef.current;
        if (prevSelected >= 0 && prevSelected < pointEntitiesRef.current.length) {
            applyVertexDefault(prevSelected);
        }
        const pointEntity = dataSourceRef.current.entities.add({
            position,
            point: {
                pixelSize: 10,
                color: pointColor,
                outlineColor: pointOutlineColor,
                outlineWidth: 2,
                heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
            },
        });
        const clamped = Math.max(0, Math.min(insertIndex, pointEntitiesRef.current.length));
        pointEntitiesRef.current.splice(clamped, 0, pointEntity);
        const newPoints = [...pointsRef.current];
        newPoints.splice(clamped, 0, coord);
        const newCartesian = [...cartesianPointsRef.current];
        newCartesian.splice(clamped, 0, position);
        const newArea = calculatePolygonAreaM2(newPoints);
        setPointsState(newPoints);
        setCartesianPoints(newCartesian);
        setAreaM2(newArea);
        selectedVertexIndexRef.current = clamped;
        setSelectedVertexIndexState(clamped);
        applyVertexHighlight(clamped);
        (_a = onPolygonChangeRef.current) === null || _a === void 0 ? void 0 : _a.call(onPolygonChangeRef, newPoints, newArea);
    }, [viewer, pointColor, pointOutlineColor, applyVertexDefault, applyVertexHighlight]);
    const insertPointAtRef = useRef(insertPointAt);
    useEffect(() => { insertPointAtRef.current = insertPointAt; }, [insertPointAt]);
    // Add a point (uses refs to avoid dependency on state)
    const addPoint = useCallback((position) => {
        var _a;
        if (!dataSourceRef.current || !viewer || viewer.isDestroyed())
            return;
        if (transformPointRef.current)
            position = transformPointRef.current(position);
        const cartographic = Cesium.Cartographic.fromCartesian(position);
        const coord = {
            lat: Cesium.Math.toDegrees(cartographic.latitude),
            lng: Cesium.Math.toDegrees(cartographic.longitude)
        };
        // If another vertex was selected, drop its highlight — the newly placed
        // vertex always becomes the active one (matches path tool behaviour).
        const prevSelected = selectedVertexIndexRef.current;
        if (prevSelected >= 0 && prevSelected < pointEntitiesRef.current.length) {
            applyVertexDefault(prevSelected);
        }
        // Add point marker — new vertex starts in default styling; it gets the
        // active highlight applied via setSelectedVertexIndex below.
        const pointEntity = dataSourceRef.current.entities.add({
            position,
            point: {
                pixelSize: 10,
                color: pointColor,
                outlineColor: pointOutlineColor,
                outlineWidth: 2,
                heightReference: Cesium.HeightReference.CLAMP_TO_GROUND
            }
        });
        pointEntitiesRef.current.push(pointEntity);
        // Update state using refs for current values
        const newPoints = [...pointsRef.current, coord];
        const newCartesian = [...cartesianPointsRef.current, position];
        const newArea = calculatePolygonAreaM2(newPoints);
        setPointsState(newPoints);
        setCartesianPoints(newCartesian);
        setAreaM2(newArea);
        // New vertex becomes the selected/active one.
        const newIndex = pointEntitiesRef.current.length - 1;
        selectedVertexIndexRef.current = newIndex;
        setSelectedVertexIndexState(newIndex);
        applyVertexHighlight(newIndex);
        (_a = onPolygonChangeRef.current) === null || _a === void 0 ? void 0 : _a.call(onPolygonChangeRef, newPoints, newArea);
    }, [viewer, pointColor, pointOutlineColor, applyVertexDefault, applyVertexHighlight]);
    // Remove a point by index (uses refs to avoid dependency on state)
    const removePoint = useCallback((index) => {
        var _a;
        if (!dataSourceRef.current)
            return;
        // Remove point entity
        const entity = pointEntitiesRef.current[index];
        if (entity) {
            dataSourceRef.current.entities.remove(entity);
        }
        pointEntitiesRef.current.splice(index, 1);
        // Indices have shifted; let the next MOUSE_MOVE recompute the hovered
        // vertex from scratch rather than risk a stale index re-applying
        // default styling to a vertex that was never hovered.
        hoveredVertexIndexRef.current = -1;
        // Selection bookkeeping: a deleted selection moves to its previous ring
        // neighbour so the active node stays where the user was working; higher
        // indices shift down by one to follow the array.
        const sel = selectedVertexIndexRef.current;
        const newLength = pointEntitiesRef.current.length;
        let nextSel = sel;
        if (sel === index) {
            nextSel = newLength > 0 ? (index - 1 + newLength) % newLength : -1;
        }
        else if (sel > index) {
            nextSel = sel - 1;
        }
        if (nextSel !== sel) {
            selectedVertexIndexRef.current = nextSel;
            setSelectedVertexIndexState(nextSel);
        }
        if (sel === index && nextSel !== -1) {
            applyVertexHighlight(nextSel);
        }
        // Update state using refs for current values
        const newPoints = pointsRef.current.filter((_, i) => i !== index);
        const newCartesian = cartesianPointsRef.current.filter((_, i) => i !== index);
        const newArea = calculatePolygonAreaM2(newPoints);
        setPointsState(newPoints);
        setCartesianPoints(newCartesian);
        setAreaM2(newArea);
        (_a = onPolygonChangeRef.current) === null || _a === void 0 ? void 0 : _a.call(onPolygonChangeRef, newPoints, newArea);
    }, [applyVertexHighlight]);
    // Update point position (for dragging) - uses refs to avoid dependency on state
    const updatePointPosition = useCallback((index, newPosition) => {
        var _a;
        if (!dataSourceRef.current)
            return;
        if (transformPointRef.current)
            newPosition = transformPointRef.current(newPosition);
        const cartographic = Cesium.Cartographic.fromCartesian(newPosition);
        const coord = {
            lat: Cesium.Math.toDegrees(cartographic.latitude),
            lng: Cesium.Math.toDegrees(cartographic.longitude)
        };
        // Update point entity position
        const entity = pointEntitiesRef.current[index];
        if (entity) {
            entity.position = new Cesium.ConstantPositionProperty(newPosition);
        }
        // Update state using refs for current values
        const newPoints = [...pointsRef.current];
        newPoints[index] = coord;
        const newCartesian = [...cartesianPointsRef.current];
        newCartesian[index] = newPosition;
        const newArea = calculatePolygonAreaM2(newPoints);
        setPointsState(newPoints);
        setCartesianPoints(newCartesian);
        setAreaM2(newArea);
        (_a = onPolygonChangeRef.current) === null || _a === void 0 ? void 0 : _a.call(onPolygonChangeRef, newPoints, newArea);
    }, []);
    useEffect(() => { addPointRef.current = addPoint; }, [addPoint]);
    useEffect(() => { removePointRef.current = removePoint; }, [removePoint]);
    useEffect(() => { updatePointPositionRef.current = updatePointPosition; }, [updatePointPosition]);
    // Clear all points. Removes vertex + polygon entities individually so the
    // long-lived outline polyline and phantom-vertex entities (which back the
    // edge-hover affordance) survive — those are owned by the data-source
    // init effect and only torn down on viewer change.
    const clearAll = useCallback(() => {
        var _a;
        if (dataSourceRef.current) {
            pointEntitiesRef.current.forEach(e => dataSourceRef.current.entities.remove(e));
            if (polygonEntityRef.current) {
                dataSourceRef.current.entities.remove(polygonEntityRef.current);
            }
        }
        pointEntitiesRef.current = [];
        polygonEntityRef.current = null;
        selectedVertexIndexRef.current = -1;
        hoveredVertexIndexRef.current = -1;
        setSelectedVertexIndexState(-1);
        if (phantomEntityRef.current)
            phantomEntityRef.current.show = false;
        phantomTargetRef.current = null;
        // Reset the position refs synchronously, not just the state. The outline
        // polyline's positions are a per-frame CallbackProperty that reads
        // cartesianPointsRef.current directly; the state->ref sync effects don't
        // run until the next render (or never, if we unmount first), so without
        // this the red perimeter outline keeps drawing the old vertices after a
        // clear/cancel until a re-render lands.
        pointsRef.current = [];
        cartesianPointsRef.current = [];
        setPointsState([]);
        setCartesianPoints([]);
        setAreaM2(0);
        clearRectPreviewRef.current();
        (_a = onPolygonChangeRef.current) === null || _a === void 0 ? void 0 : _a.call(onPolygonChangeRef, [], 0);
    }, []);
    // Remove the last point
    const removeLastPoint = useCallback(() => {
        if (pointsRef.current.length > 0) {
            removePoint(pointsRef.current.length - 1);
        }
    }, [removePoint]);
    // Remove whichever vertex is currently selected (if any). Wired to the
    // bottom panel's Delete-vertex button and to Backspace/Delete keyboard.
    const deleteSelectedVertex = useCallback(() => {
        const idx = selectedVertexIndexRef.current;
        if (idx < 0 || idx >= pointEntitiesRef.current.length)
            return;
        removePoint(idx);
    }, [removePoint]);
    // Set points programmatically
    const setPoints = useCallback((coords) => {
        var _a;
        if (!dataSourceRef.current || !viewer || viewer.isDestroyed())
            return;
        // Clear existing vertex + polygon entities only — preserve the outline
        // polyline and phantom entities (managed by the data-source init effect).
        pointEntitiesRef.current.forEach(e => dataSourceRef.current.entities.remove(e));
        if (polygonEntityRef.current) {
            dataSourceRef.current.entities.remove(polygonEntityRef.current);
        }
        pointEntitiesRef.current = [];
        polygonEntityRef.current = null;
        selectedVertexIndexRef.current = -1;
        hoveredVertexIndexRef.current = -1;
        setSelectedVertexIndexState(-1);
        if (phantomEntityRef.current)
            phantomEntityRef.current.show = false;
        phantomTargetRef.current = null;
        // Add new points
        const newCartesian = [];
        coords.forEach(coord => {
            const position = Cesium.Cartesian3.fromDegrees(coord.lng, coord.lat);
            newCartesian.push(position);
            const pointEntity = dataSourceRef.current.entities.add({
                position,
                point: {
                    pixelSize: 10,
                    color: pointColor,
                    outlineColor: pointOutlineColor,
                    outlineWidth: 2,
                    heightReference: Cesium.HeightReference.CLAMP_TO_GROUND
                }
            });
            pointEntitiesRef.current.push(pointEntity);
        });
        const newArea = calculatePolygonAreaM2(coords);
        setPointsState(coords);
        setCartesianPoints(newCartesian);
        setAreaM2(newArea);
        (_a = onPolygonChangeRef.current) === null || _a === void 0 ? void 0 : _a.call(onPolygonChangeRef, coords, newArea);
    }, [viewer, pointColor, pointOutlineColor]);
    // Axis-aligned rectangle corners (ring order) from two opposite corners.
    const rectangleCorners = useCallback((a, c) => [
        { lat: a.lat, lng: a.lng },
        { lat: a.lat, lng: c.lng },
        { lat: c.lat, lng: c.lng },
        { lat: c.lat, lng: a.lng },
    ], []);
    // Tear down the rectangle rubber-band preview + reset its pending state.
    const clearRectPreview = useCallback(() => {
        const ds = dataSourceRef.current;
        if (ds) {
            if (rectFillEntityRef.current)
                ds.entities.remove(rectFillEntityRef.current);
            if (rectOutlineEntityRef.current)
                ds.entities.remove(rectOutlineEntityRef.current);
            if (rectMarkerEntityRef.current)
                ds.entities.remove(rectMarkerEntityRef.current);
            if (rectWidthLabelRef.current)
                ds.entities.remove(rectWidthLabelRef.current);
            if (rectHeightLabelRef.current)
                ds.entities.remove(rectHeightLabelRef.current);
        }
        rectFillEntityRef.current = null;
        rectOutlineEntityRef.current = null;
        rectMarkerEntityRef.current = null;
        rectWidthLabelRef.current = null;
        rectHeightLabelRef.current = null;
        rectCornerARef.current = null;
        rectCursorRef.current = null;
        setRectanglePending(false);
        setAreaM2(0);
    }, []);
    // Create the rectangle preview entities (fill + outline + corner-A marker)
    // once the first corner is placed. Geometry is CallbackProperty-driven from
    // rectCornerARef/rectCursorRef so it tracks the cursor each frame. Also keeps
    // the live area HUD (areaM2) in sync as the cursor moves.
    const ensureRectPreview = useCallback(() => {
        const ds = dataSourceRef.current;
        if (!ds || !viewer || viewer.isDestroyed())
            return;
        const cornersCartesian = () => {
            const a = rectCornerARef.current;
            const c = rectCursorRef.current;
            if (!a || !c)
                return [];
            return rectangleCorners(a, c).map(p => Cesium.Cartesian3.fromDegrees(p.lng, p.lat));
        };
        if (!rectFillEntityRef.current) {
            rectFillEntityRef.current = ds.entities.add({
                polygon: {
                    hierarchy: new Cesium.CallbackProperty(() => new Cesium.PolygonHierarchy(cornersCartesian()), false),
                    material: polygonFillColor,
                    outline: false,
                    heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
                },
            });
        }
        if (!rectOutlineEntityRef.current) {
            rectOutlineEntityRef.current = ds.entities.add({
                polyline: {
                    positions: new Cesium.CallbackProperty(() => {
                        const c = cornersCartesian();
                        return c.length ? [...c, c[0]] : [];
                    }, false),
                    width: makePolylineWidthProperty(2, viewer),
                    material: new Cesium.ColorMaterialProperty(polygonOutlineColor),
                    clampToGround: true,
                },
            });
        }
        if (!rectMarkerEntityRef.current) {
            rectMarkerEntityRef.current = ds.entities.add({
                position: new Cesium.CallbackProperty(() => {
                    const a = rectCornerARef.current;
                    return a ? Cesium.Cartesian3.fromDegrees(a.lng, a.lat) : Cesium.Cartesian3.ZERO;
                }, false),
                point: {
                    pixelSize: 10,
                    color: pointColor,
                    outlineColor: pointOutlineColor,
                    outlineWidth: 2,
                    heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
                },
            });
        }
        // Width (top edge: corner 0→1) + height (left edge: corner 0→3) labels.
        if (!rectWidthLabelRef.current) {
            rectWidthLabelRef.current = addLengthLabel(ds, () => { const c = cornersCartesian(); return c.length === 4 ? Cesium.Cartesian3.midpoint(c[0], c[1], new Cesium.Cartesian3()) : Cesium.Cartesian3.ZERO; }, () => { const c = cornersCartesian(); return c.length === 4 ? formatDistance(Cesium.Cartesian3.distance(c[0], c[1]), lengthUnitRef.current) : ''; }, () => showLabelsRef.current);
        }
        if (!rectHeightLabelRef.current) {
            rectHeightLabelRef.current = addLengthLabel(ds, () => { const c = cornersCartesian(); return c.length === 4 ? Cesium.Cartesian3.midpoint(c[0], c[3], new Cesium.Cartesian3()) : Cesium.Cartesian3.ZERO; }, () => { const c = cornersCartesian(); return c.length === 4 ? formatDistance(Cesium.Cartesian3.distance(c[0], c[3]), lengthUnitRef.current) : ''; }, () => showLabelsRef.current);
        }
    }, [viewer, polygonFillColor, polygonOutlineColor, pointColor, pointOutlineColor, rectangleCorners]);
    const clearRectPreviewRef = useRef(clearRectPreview);
    useEffect(() => { clearRectPreviewRef.current = clearRectPreview; }, [clearRectPreview]);
    const ensureRectPreviewRef = useRef(ensureRectPreview);
    useEffect(() => { ensureRectPreviewRef.current = ensureRectPreview; }, [ensureRectPreview]);
    const rectangleCornersRef = useRef(rectangleCorners);
    useEffect(() => { rectangleCornersRef.current = rectangleCorners; }, [rectangleCorners]);
    // When the draw mode changes (or the tool deactivates), drop any in-progress
    // rectangle so a stale corner can't leak across modes.
    useEffect(() => {
        drawModeRef.current = drawMode;
        if (drawMode !== 'rectangle')
            clearRectPreview();
    }, [drawMode, clearRectPreview]);
    // Set up event handlers - stable callbacks using refs
    useEffect(() => {
        if (!viewer || viewer.isDestroyed() || !isActive)
            return;
        const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
        handlerRef.current = handler;
        // Click to add point or toggle-select an existing vertex. Selection
        // model mirrors the path tool: clicking a vertex selects it (yellow);
        // clicking the same vertex again deselects. Delete is now an explicit
        // action (panel button / keyboard) rather than a side-effect of click.
        handler.setInputAction((event) => {
            var _a, _b;
            if (isDraggingRef.current)
                return;
            // Rectangle mode: two clicks define opposite corners. No vertex/point
            // accounting — the first click anchors corner A (with a live rubber-band
            // preview), the second hands four corners to the consumer to build the
            // finished polygon.
            if (drawModeRef.current === 'rectangle') {
                const ray = viewer.camera.getPickRay(event.position);
                let pos;
                if (ray)
                    pos = viewer.scene.globe.pick(ray, viewer.scene);
                if (!pos)
                    pos = viewer.camera.pickEllipsoid(event.position, viewer.scene.globe.ellipsoid);
                if (!pos)
                    return;
                const carto = Cesium.Cartographic.fromCartesian(pos);
                const coord = {
                    lat: Cesium.Math.toDegrees(carto.latitude),
                    lng: Cesium.Math.toDegrees(carto.longitude),
                };
                if (!rectCornerARef.current) {
                    rectCornerARef.current = coord;
                    rectCursorRef.current = coord;
                    setRectanglePending(true);
                    ensureRectPreviewRef.current();
                }
                else {
                    const corners = rectangleCornersRef.current(rectCornerARef.current, coord);
                    clearRectPreviewRef.current();
                    (_a = onRectangleCompleteRef.current) === null || _a === void 0 ? void 0 : _a.call(onRectangleCompleteRef, corners);
                }
                return;
            }
            // Double-click guard: skip the spurious second click during a double-click
            if (doubleClickGuardRef.current) {
                doubleClickGuardRef.current = false;
                return;
            }
            // Vertex selection — screen-space proximity FIRST. scene.pick can miss
            // CLAMP_TO_GROUND vertex points (the polygon fill / outline shadow them at
            // ground level), which let a click on a node fall through to add-point and
            // never highlight. Proximity is reliable no matter what the raw pick hits.
            {
                const VERTEX_CLICK_PX = 15;
                let best = VERTEX_CLICK_PX;
                let bestIdx = -1;
                for (let i = 0; i < cartesianPointsRef.current.length; i++) {
                    const sp = Cesium.SceneTransforms.worldToWindowCoordinates(viewer.scene, cartesianPointsRef.current[i]);
                    if (!sp)
                        continue;
                    const dx = event.position.x - sp.x;
                    const dy = event.position.y - sp.y;
                    const d = Math.sqrt(dx * dx + dy * dy);
                    if (d < best) {
                        best = d;
                        bestIdx = i;
                    }
                }
                if (bestIdx !== -1) {
                    // Toggle selection: same vertex → deselect; different → switch active.
                    const prev = selectedVertexIndexRef.current;
                    if (prev >= 0 && prev < pointEntitiesRef.current.length)
                        applyVertexDefault(prev);
                    if (prev === bestIdx) {
                        selectedVertexIndexRef.current = -1;
                        setSelectedVertexIndexState(-1);
                    }
                    else {
                        selectedVertexIndexRef.current = bestIdx;
                        setSelectedVertexIndexState(bestIdx);
                        applyVertexHighlight(bestIdx);
                    }
                    return;
                }
            }
            // Otherwise: phantom-vertex click (insert on hovered edge) or parent
            // escalation. The phantom only paints mid-edge (away from vertices), so
            // the proximity check above never steals its clicks.
            const pickedObject = viewer.scene.pick(event.position);
            if (pickedObject === null || pickedObject === void 0 ? void 0 : pickedObject.id) {
                const clickedEntity = pickedObject.id;
                if (clickedEntity === phantomEntityRef.current &&
                    phantomTargetRef.current) {
                    const target = phantomTargetRef.current;
                    insertPointAtRef.current(target.edgeIndex + 1, target.position);
                    if (phantomEntityRef.current)
                        phantomEntityRef.current.show = false;
                    phantomTargetRef.current = null;
                    return;
                }
                // If a non-drawing entity was clicked, let parent handle it. The
                // outline polyline is internal to this hook; never escalate.
                if (clickedEntity !== outlinePolylineRef.current &&
                    ((_b = onEntityClickRef.current) === null || _b === void 0 ? void 0 : _b.call(onEntityClickRef, clickedEntity, event.position))) {
                    return;
                }
            }
            // Pick terrain position
            const ray = viewer.camera.getPickRay(event.position);
            let position;
            if (ray) {
                position = viewer.scene.globe.pick(ray, viewer.scene);
            }
            if (!position) {
                position = viewer.camera.pickEllipsoid(event.position, viewer.scene.globe.ellipsoid);
            }
            if (position) {
                // Active-node anchor (parity with the path tool's "middle-node-selected
                // → insert between selected and next-in-sequence"): if an EARLIER vertex
                // is selected, the click inserts right after it in ring order instead of
                // appending. The active/last vertex (the drawing tail) and the no-
                // selection case fall through to append, preserving the normal sketch
                // gesture for both draw directions.
                const sel = selectedVertexIndexRef.current;
                const pts = pointsRef.current;
                if (sel >= 0 && sel < pts.length - 1 && pts.length >= 3) {
                    insertPointAtRef.current(sel + 1, position);
                }
                else {
                    addPointRef.current(position);
                }
            }
        }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
        // Double-click to finish polygon (freeform only — rectangle auto-finishes
        // on its second click).
        handler.setInputAction(() => {
            var _a;
            if (drawModeRef.current === 'rectangle')
                return;
            doubleClickGuardRef.current = true;
            // Remove the point just added by the preceding click
            if (pointsRef.current.length > 0) {
                removePointRef.current(pointsRef.current.length - 1);
            }
            if (pointsRef.current.length >= 3) {
                (_a = onFinishRequestedRef.current) === null || _a === void 0 ? void 0 : _a.call(onFinishRequestedRef);
            }
            // Reset guard after a short timeout to prevent permanently blocking clicks
            setTimeout(() => { doubleClickGuardRef.current = false; }, 50);
        }, Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
        // Right-click to remove a point during drawing. Direct entity pick
        // first; if that misses, fall back to nearest vertex within a 25px
        // screen-space radius so near-misses still hit (parity with path tool).
        handler.setInputAction((event) => {
            // Rectangle mode: right-click cancels the in-progress rectangle (the
            // misplaced first corner) instead of deleting a vertex.
            if (drawModeRef.current === 'rectangle') {
                clearRectPreviewRef.current();
                return;
            }
            const pickedObject = viewer.scene.pick(event.position);
            if (pickedObject === null || pickedObject === void 0 ? void 0 : pickedObject.id) {
                const pointIndex = pointEntitiesRef.current.findIndex(e => e === pickedObject.id);
                if (pointIndex !== -1) {
                    removePointRef.current(pointIndex);
                    return;
                }
            }
            const SCREEN_THRESHOLD = 25;
            let closestDist = SCREEN_THRESHOLD;
            let closestIndex = -1;
            for (let i = 0; i < cartesianPointsRef.current.length; i++) {
                const screenPos = Cesium.SceneTransforms.worldToWindowCoordinates(viewer.scene, cartesianPointsRef.current[i]);
                if (!screenPos)
                    continue;
                const dx = event.position.x - screenPos.x;
                const dy = event.position.y - screenPos.y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                if (dist < closestDist) {
                    closestDist = dist;
                    closestIndex = i;
                }
            }
            if (closestIndex !== -1) {
                removePointRef.current(closestIndex);
            }
        }, Cesium.ScreenSpaceEventType.RIGHT_CLICK);
        // Mouse down to start dragging. Same pick-fallback as LEFT_CLICK: if
        // the outline polyline shadows a vertex, do a screen-space proximity
        // check so the drag still latches to the underlying vertex.
        handler.setInputAction((event) => {
            // Rectangle mode has no draggable vertices mid-draw — skip latch entirely.
            if (drawModeRef.current === 'rectangle')
                return;
            // Latch the drag to the nearest vertex by screen-space proximity — the
            // raw scene.pick misses CLAMP_TO_GROUND points shadowed by the fill /
            // outline, which broke vertex dragging once the polygon had a fill.
            let pointIndex = -1;
            {
                const VERTEX_DRAG_PX = 15;
                let best = VERTEX_DRAG_PX;
                for (let i = 0; i < cartesianPointsRef.current.length; i++) {
                    const sp = Cesium.SceneTransforms.worldToWindowCoordinates(viewer.scene, cartesianPointsRef.current[i]);
                    if (!sp)
                        continue;
                    const dx = event.position.x - sp.x;
                    const dy = event.position.y - sp.y;
                    const d = Math.sqrt(dx * dx + dy * dy);
                    if (d < best) {
                        best = d;
                        pointIndex = i;
                    }
                }
            }
            if (pointIndex !== -1) {
                // Set the ref synchronously (not just via the state→effect mirror)
                // so the LEFT_UP cleanup and LEFT_CLICK guard below see the true
                // drag state within the same down→up→click gesture. Relying on the
                // async useEffect mirror let a quick click read stale state, which
                // swallowed vertex-select clicks and left a vertex "stuck" to the
                // cursor (warping the polygon on the next move).
                isDraggingRef.current = true;
                setIsDragging(true);
                draggedIndexRef.current = pointIndex;
                viewer.scene.screenSpaceCameraController.enableInputs = false;
            }
        }, Cesium.ScreenSpaceEventType.LEFT_DOWN);
        // Mouse move: handles vertex drag, vertex hover highlight, AND
        // phantom-on-edge hover painting.
        handler.setInputAction((event) => {
            // Rectangle mode: while a first corner is placed, track the cursor as the
            // opposite corner and keep the live area in sync. The preview geometry
            // reads rectCursorRef per-frame via CallbackProperty.
            if (drawModeRef.current === 'rectangle') {
                if (!rectCornerARef.current)
                    return;
                const rRay = viewer.camera.getPickRay(event.endPosition);
                let rPos;
                if (rRay)
                    rPos = viewer.scene.globe.pick(rRay, viewer.scene);
                if (!rPos)
                    rPos = viewer.camera.pickEllipsoid(event.endPosition, viewer.scene.globe.ellipsoid);
                if (rPos) {
                    const carto = Cesium.Cartographic.fromCartesian(rPos);
                    rectCursorRef.current = {
                        lat: Cesium.Math.toDegrees(carto.latitude),
                        lng: Cesium.Math.toDegrees(carto.longitude),
                    };
                    setAreaM2(calculatePolygonAreaM2(rectangleCornersRef.current(rectCornerARef.current, rectCursorRef.current)));
                }
                return;
            }
            // Resolve cursor → terrain (used by both branches).
            const ray = viewer.camera.getPickRay(event.endPosition);
            let position;
            if (ray) {
                position = viewer.scene.globe.pick(ray, viewer.scene);
            }
            if (!position) {
                position = viewer.camera.pickEllipsoid(event.endPosition, viewer.scene.globe.ellipsoid);
            }
            // Vertex drag wins — phantom paints only when the cursor is
            // freely hovering, not while dragging an existing vertex.
            if (isDraggingRef.current && draggedIndexRef.current !== -1) {
                if (position) {
                    updatePointPositionRef.current(draggedIndexRef.current, position);
                }
                if (phantomEntityRef.current)
                    phantomEntityRef.current.show = false;
                phantomTargetRef.current = null;
                return;
            }
            // Find the nearest vertex in screen space. Used for two things:
            // (1) showing green hover styling on the vertex under the cursor,
            // (2) suppressing the phantom when the cursor is within ~15px of a
            //     vertex so the vertex pick wins cleanly on click.
            const VERTEX_HOVER_PX = 15;
            let nearestVertexIdx = -1;
            let nearestVertexDist = VERTEX_HOVER_PX;
            for (let i = 0; i < cartesianPointsRef.current.length; i++) {
                const sp = Cesium.SceneTransforms.worldToWindowCoordinates(viewer.scene, cartesianPointsRef.current[i]);
                if (!sp)
                    continue;
                const dx = event.endPosition.x - sp.x;
                const dy = event.endPosition.y - sp.y;
                const d = Math.sqrt(dx * dx + dy * dy);
                if (d < nearestVertexDist) {
                    nearestVertexDist = d;
                    nearestVertexIdx = i;
                }
            }
            // Apply hover styling. Selected vertex always wins (stays yellow);
            // we only paint hover on non-selected vertices.
            const prevHover = hoveredVertexIndexRef.current;
            const sel = selectedVertexIndexRef.current;
            const nextHover = nearestVertexIdx === sel ? -1 : nearestVertexIdx;
            if (prevHover !== nextHover) {
                if (prevHover >= 0 && prevHover !== sel && prevHover < pointEntitiesRef.current.length) {
                    applyVertexDefault(prevHover);
                }
                if (nextHover >= 0) {
                    applyVertexHover(nextHover);
                }
                hoveredVertexIndexRef.current = nextHover;
            }
            // Phantom on edge hover: show a ghost vertex at the cursor's
            // projection while the cursor is over the outline polyline AND
            // not close to an existing vertex (otherwise the phantom would
            // shadow the vertex and steal clicks).
            const phantom = phantomEntityRef.current;
            if (!phantom || !outlinePolylineRef.current || !isActiveRef.current) {
                return;
            }
            if (nearestVertexIdx !== -1) {
                phantom.show = false;
                phantomTargetRef.current = null;
                return;
            }
            const overObj = viewer.scene.pick(event.endPosition);
            const onOutline = (overObj === null || overObj === void 0 ? void 0 : overObj.id) === outlinePolylineRef.current;
            if (!onOutline || !position || cartesianPointsRef.current.length < 2) {
                phantom.show = false;
                phantomTargetRef.current = null;
                return;
            }
            const edgeIndex = findNearestEdge(position);
            if (edgeIndex < 0) {
                phantom.show = false;
                phantomTargetRef.current = null;
                return;
            }
            phantom.position = new Cesium.ConstantPositionProperty(position);
            phantom.show = true;
            phantomTargetRef.current = { edgeIndex, position };
        }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
        // Mouse up to end dragging
        handler.setInputAction(() => {
            if (isDraggingRef.current) {
                isDraggingRef.current = false;
                setIsDragging(false);
                draggedIndexRef.current = -1;
                viewer.scene.screenSpaceCameraController.enableInputs = true;
            }
        }, Cesium.ScreenSpaceEventType.LEFT_UP);
        return () => {
            if (handlerRef.current && !handlerRef.current.isDestroyed()) {
                handlerRef.current.destroy();
            }
            handlerRef.current = null;
        };
    }, [viewer, isActive]);
    // Cleanup when deactivated
    useEffect(() => {
        if (!isActive && viewer && !viewer.isDestroyed()) {
            viewer.scene.screenSpaceCameraController.enableInputs = true;
            isDraggingRef.current = false;
            setIsDragging(false);
            draggedIndexRef.current = -1;
            clearRectPreviewRef.current();
        }
    }, [isActive, viewer]);
    return {
        points,
        cartesianPoints,
        areaM2,
        isDragging,
        selectedVertexIndex,
        rectanglePending,
        polygonEntityRef,
        clearAll,
        setPoints,
        removeLastPoint,
        deleteSelectedVertex
    };
};
